/**
 * Header JavaScript - Unit 23 Skatepark Theme
 *
 * Handles interactive functionality:
 * - Mobile drawer menu
 * - Expandable search
 * - Keyboard navigation
 * - Focus management
 */

class HeaderDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-drawer]');
    this.drawerToggle = document.querySelector('[data-drawer-toggle]');
    this.drawerClose = document.querySelector('[data-drawer-close]');
    this.overlay = document.querySelector('[data-drawer-overlay]');

    if (!this.drawer) return;

    this.init();
  }

  init() {
    // Toggle drawer
    if (this.drawerToggle) {
      this.drawerToggle.addEventListener('click', () => this.open());
    }

    // Close drawer
    if (this.drawerClose) {
      this.drawerClose.addEventListener('click', () => this.close());
    }

    // Close on overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });

    // Trap focus in drawer when open
    this.drawer.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && this.isOpen()) {
        this.trapFocus(e);
      }
    });
  }

  open() {
    this.drawer.classList.add('active');
    if (this.overlay) this.overlay.classList.add('active');
    document.body.classList.add('no-scroll');

    // Set aria and focus
    this.drawer.setAttribute('aria-hidden', 'false');
    this.drawerToggle?.setAttribute('aria-expanded', 'true');

    // Focus first focusable element in drawer
    const firstFocusable = this.drawer.querySelector('a, button, input, [tabindex]:not([tabindex="-1"])');
    if (firstFocusable) {
      setTimeout(() => firstFocusable.focus(), 100);
    }
  }

  close() {
    this.drawer.classList.remove('active');
    if (this.overlay) this.overlay.classList.remove('active');
    document.body.classList.remove('no-scroll');

    // Set aria and return focus
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawerToggle?.setAttribute('aria-expanded', 'false');
    this.drawerToggle?.focus();
  }

  isOpen() {
    return this.drawer.classList.contains('active');
  }

  trapFocus(e) {
    const focusableElements = this.drawer.querySelectorAll(
      'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement.focus();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement.focus();
    }
  }
}

class HeaderSearch {
  constructor() {
    // Mobile search
    this.mobileToggle = document.querySelector('[data-search-toggle-mobile]');
    this.mobileContainer = document.querySelector('[data-search-container-mobile]');
    this.mobileInput = document.querySelector('[data-search-input-mobile]');

    // Desktop search
    this.desktopToggle = document.querySelector('[data-search-toggle-desktop]');
    this.desktopContainer = document.querySelector('[data-search-container-desktop]');
    this.desktopInput = document.querySelector('[data-search-input-desktop]');

    this.init();
  }

  init() {
    // Mobile search toggle
    if (this.mobileToggle && this.mobileContainer) {
      this.mobileToggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleMobile();
      });
    }

    // Desktop search toggle
    if (this.desktopToggle && this.desktopContainer) {
      this.desktopToggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleDesktop();
      });
    }

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (this.isMobileOpen()) this.closeMobile();
        if (this.isDesktopOpen()) this.closeDesktop();
      }
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (this.mobileContainer && this.isMobileOpen() &&
          !this.mobileContainer.contains(e.target) &&
          !this.mobileToggle.contains(e.target)) {
        this.closeMobile();
      }

      if (this.desktopContainer && this.isDesktopOpen() &&
          !this.desktopContainer.contains(e.target) &&
          !this.desktopToggle.contains(e.target)) {
        this.closeDesktop();
      }
    });
  }

  toggleMobile() {
    if (this.isMobileOpen()) {
      this.closeMobile();
    } else {
      this.openMobile();
    }
  }

  toggleDesktop() {
    if (this.isDesktopOpen()) {
      this.closeDesktop();
    } else {
      this.openDesktop();
    }
  }

  openMobile() {
    this.mobileContainer.classList.add('active');
    this.mobileToggle.setAttribute('aria-expanded', 'true');
    if (this.mobileInput) {
      setTimeout(() => this.mobileInput.focus(), 100);
    }
  }

  closeMobile() {
    this.mobileContainer.classList.remove('active');
    this.mobileToggle.setAttribute('aria-expanded', 'false');
  }

  openDesktop() {
    this.desktopContainer.classList.add('active');
    this.desktopToggle.setAttribute('aria-expanded', 'true');
    if (this.desktopInput) {
      setTimeout(() => this.desktopInput.focus(), 100);
    }
  }

  closeDesktop() {
    this.desktopContainer.classList.remove('active');
    this.desktopToggle.setAttribute('aria-expanded', 'false');
  }

  isMobileOpen() {
    return this.mobileContainer && this.mobileContainer.classList.contains('active');
  }

  isDesktopOpen() {
    return this.desktopContainer && this.desktopContainer.classList.contains('active');
  }
}

class DropdownMenu {
  constructor() {
    this.dropdowns = document.querySelectorAll('[data-dropdown]');

    if (this.dropdowns.length === 0) return;

    this.init();
  }

  init() {
    this.dropdowns.forEach(dropdown => {
      const trigger = dropdown.querySelector('[data-dropdown-trigger]');
      const menu = dropdown.querySelector('[data-dropdown-menu]');

      if (!trigger || !menu) return;

      // Toggle on click
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle(dropdown);
      });

      // Close on Escape
      dropdown.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.close(dropdown);
          trigger.focus();
        }
      });

      // Keyboard navigation
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.open(dropdown);
          const firstLink = menu.querySelector('a');
          if (firstLink) firstLink.focus();
        }
      });
    });

    // Close all dropdowns on outside click
    document.addEventListener('click', (e) => {
      this.dropdowns.forEach(dropdown => {
        if (!dropdown.contains(e.target)) {
          this.close(dropdown);
        }
      });
    });
  }

  toggle(dropdown) {
    if (dropdown.classList.contains('active')) {
      this.close(dropdown);
    } else {
      // Close other dropdowns first
      this.dropdowns.forEach(d => this.close(d));
      this.open(dropdown);
    }
  }

  open(dropdown) {
    dropdown.classList.add('active');
    const trigger = dropdown.querySelector('[data-dropdown-trigger]');
    trigger?.setAttribute('aria-expanded', 'true');
  }

  close(dropdown) {
    dropdown.classList.remove('active');
    const trigger = dropdown.querySelector('[data-dropdown-trigger]');
    trigger?.setAttribute('aria-expanded', 'false');
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

function init() {
  new HeaderDrawer();
  new HeaderSearch();
  new DropdownMenu();
}
